import { useState } from 'react'
import { api } from '../../api/client'
import { useToast } from '../../hooks/useToast'
import type { Brief } from '../../types'
import { CopyButton } from './parts'

/**
 * Paste meeting notes → the model drafts a follow-up email using them plus the brief.
 *
 * The notes are deliberately not persisted as a record — HubSpot is the CRM. They are sent
 * with the request, used to draft, and dropped; only the resulting email is stored.
 */
export function Followup({
  brief,
  onGenerated,
  canEdit,
}: {
  brief: Brief
  onGenerated: (b: Brief) => void
  canEdit: boolean
}) {
  const toast = useToast()
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const email = brief.followupEmail

  async function generate() {
    if (!notes.trim()) {
      toast('Paste your meeting notes first')
      return
    }
    setBusy(true)
    try {
      onGenerated(await api.generateFollowup(brief.company, notes))
      toast('Follow-up drafted')
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't generate")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="notes-wrap">
      <div className="note-block">
        <div className="note-h">
          <span>
            <i className="ti ti-clipboard-text" /> Meeting notes
          </span>
          <span className="note-stage">Paste to generate</span>
        </div>
        <p className="note-sub">
          Paste your notes from the meeting. The AI uses them, plus this account's brief, to draft a
          follow-up email about where Burendo can help. Nothing is saved as a record here.
        </p>
        <textarea
          className="note-ta"
          id="fu-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Paste your meeting notes here…"
        />
        <div className="note-actions" style={{ marginTop: 12 }}>
          <button
            className="genbtn"
            id="fu-gen"
            style={{ margin: 0, padding: '11px 22px' }}
            onClick={generate}
            disabled={busy || !canEdit}
          >
            <i className="ti ti-sparkles" style={{ fontSize: 15 }} />{' '}
            {busy ? 'Drafting…' : 'Generate follow-up email'}
          </button>
          {busy && <span style={{ fontSize: 12.5, color: 'var(--slate)' }}>Drafting…</span>}
        </div>
      </div>

      {email && (
        <div className="fu-out">
          <div className="note-h">
            <span>
              <i className="ti ti-mail" /> Draft follow-up email
            </span>
            <CopyButton
              className="tb-btn"
              text={`${email.subject ? `Subject: ${email.subject}\n\n` : ''}${email.body}`}
              label="Copy"
              copiedLabel="Copied ✓"
            />
          </div>
          <div className="fu-email">
            {email.subject && (
              <div className="fu-subj">
                <span className="fu-lbl">Subject</span> {email.subject}
              </div>
            )}
            <pre className="fu-body">{email.body}</pre>
          </div>
          <p className="note-sub" style={{ marginTop: 10 }}>
            Generated from your notes and the account brief. Edit freely before sending. Notes are used only
            to draft this and aren't stored as a record (keep those in HubSpot).
          </p>
        </div>
      )}
    </div>
  )
}
